import {
  FlagRecord,
  InstrumentId,
  ProposedActionType,
  ScenarioClass,
  ScenarioId,
  ScenarioResult,
  SignOffAnswer,
  SourceDocument,
  TabId,
} from '../types/assessment'
import { AssistantReply, generateReply } from '../utils/aiEngine'
import { generateLegalReply } from '../utils/legalAiEngine'
import { evaluateScenarios } from '../utils/scoring'
import { evaluateLawScenarios } from '../utils/legalScoring'
import { LIBRARY_HASH, PACKAGE_ID, PACKAGE_VERSION, scenarios } from './ledger'
import { documentOptions, sourceDocuments } from './documents'
import {
  LAW_LIBRARY_HASH,
  LAW_PACKAGE_ID,
  LAW_PACKAGE_VERSION,
  lawScenarios,
} from './harrow'
import { harrowDocumentOptions, harrowDocuments } from './harrowDocuments'

// A scenario as the engine and the reviewer view need it, independent of instrument.
export interface EngineScenario {
  id: ScenarioId
  rowId: string
  klass: ScenarioClass
  vehicle: string
  decisiveSourceId: string
  correctAction: string
  fullCredit: string
}

export interface InstrumentConfig {
  id: InstrumentId
  version: string
  hash: string
  name: string
  role: string
  matter: string
  libraryLabel: string
  tabs: { id: TabId; label: string }[]
  defaultTab: TabId
  documents: SourceDocument[]
  documentOptions: { id: string; label: string }[]
  scenarios: EngineScenario[]
  assistant: {
    name: string
    subtitle: string
    notice: string
    emptyHint: string
    chips: string[]
    placeholder: string
    shareable: boolean
  }
  drawer: {
    whatLabel: string
    sourcesLabel: string
    actions: { id: ProposedActionType; label: string; hint: string }[]
    entryActions: ProposedActionType[]
    escalateActions: ProposedActionType[]
    escalateToLabel: string
    amountLabel: string
    signOffLegend: string
    signOffs: { id: SignOffAnswer; label: string }[]
    flagLabel: string
    correctLabel: string
  }
  reflection: {
    heading: string
    intro: string
    questions: { key: 'q1' | 'q2' | 'q3' | 'q4'; label: string }[]
    confidenceLegend: string
    confidence: { id: 'yes' | 'with-flags' | 'no'; label: string }[]
    submitLabel: string
    entriesNote: string
    incompleteNote: string
  }
  reply: (input: string, priorPrompts: number, sharedDocs: string[]) => AssistantReply
  evaluate: (flags: Record<string, FlagRecord>, openedDocs: string[]) => ScenarioResult[]
}

const FIN: InstrumentConfig = {
  id: 'FIN-001',
  version: PACKAGE_VERSION,
  hash: LIBRARY_HASH,
  name: 'Financial close',
  role: 'Financial Controller',
  matter: 'Meridian SAS · Q2 2026 close',
  libraryLabel: 'FIN-001 · Financial close',
  tabs: [
    { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'budget', label: 'P&L vs Budget' },
    { id: 'documents', label: 'Source documents' },
  ],
  defaultTab: 'reconciliation',
  documents: sourceDocuments,
  documentOptions,
  scenarios: scenarios.map((s) => ({
    id: s.id,
    rowId: s.rowId,
    klass: s.klass,
    vehicle: s.vehicle,
    decisiveSourceId: s.decisiveSourceId,
    correctAction: s.correctAction,
    fullCredit: s.fullCredit,
  })),
  assistant: {
    name: 'Close Assistant',
    subtitle: 'Accounting knowledge · no access to your workspace',
    notice:
      'cannot see the ledger, the P&L pack or the source documents. It knows only what you paste into this chat.',
    emptyHint: 'Use it as much or as little as you want. There is no penalty and no bonus.',
    chips: [
      "Paste ledger rows and I'll audit them for discrepancies",
      'Explain the variance drivers in this P&L',
      'Draft the reconciliation report structure',
    ],
    placeholder: 'Paste ledger rows or ask a question. Shift + Enter for a new line.',
    shareable: false,
  },
  drawer: {
    whatLabel: 'What is wrong?',
    sourcesLabel: 'Which source establishes this?',
    actions: [
      { id: 'adjusting', label: 'Adjusting entry', hint: 'Dr / Cr / amount / narrative' },
      { id: 'reclass', label: 'Reclassification', hint: 'Dr / Cr / amount / narrative' },
      { id: 'escalate', label: 'Escalate', hint: 'To whom, and why' },
      { id: 'note', label: 'Note only', hint: 'Record the observation, take no action' },
    ],
    entryActions: ['adjusting', 'reclass'],
    escalateActions: ['escalate'],
    escalateToLabel: 'Escalate to',
    amountLabel: 'Amount (EUR)',
    signOffLegend: 'Would you sign off on this line as it now stands?',
    signOffs: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'escalate', label: 'Escalate' },
    ],
    flagLabel: 'Flag',
    correctLabel: 'Correct',
  },
  reflection: {
    heading: 'Reflection',
    intro: 'All three answers are required. Write as much or as little as you need.',
    questions: [
      {
        key: 'q1',
        label:
          'What did you check against source documents? Name the documents and the specific assertions you tested.',
      },
      {
        key: 'q2',
        label: 'What did you accept from the assistant, or from the pack, without checking — and why?',
      },
      {
        key: 'q3',
        label: 'What would you verify next if you had 30 more minutes? Rank by risk, not by ease.',
      },
    ],
    confidenceLegend: 'Ready to submit this close pack to the Group Controller?',
    confidence: [
      { id: 'yes', label: 'Yes' },
      { id: 'with-flags', label: 'With the flags noted' },
      { id: 'no', label: 'No, material issues remain' },
    ],
    submitLabel: 'Submit close pack',
    entriesNote: 'Entries stay editable from the ledger and P&L tabs until you submit.',
    incompleteNote: 'Answer all three questions and choose a sign-off to enable submission.',
  },
  reply: (input, priorPrompts) => generateReply(input, priorPrompts),
  evaluate: evaluateScenarios,
}

const LAW: InstrumentConfig = {
  id: 'LAW-001',
  version: LAW_PACKAGE_VERSION,
  hash: LAW_LIBRARY_HASH,
  name: 'Corporate document review',
  role: 'Trainee Solicitor',
  matter: 'Project Harrow · Ashworth Capital / Brindle Systems',
  libraryLabel: 'LAW-001 · Corporate document review · trainee level',
  tabs: [
    { id: 'bundle', label: 'Bundle review' },
    { id: 'instructions', label: 'Instructions & checklist' },
    { id: 'documents', label: 'Source documents' },
  ],
  defaultTab: 'bundle',
  documents: harrowDocuments,
  documentOptions: harrowDocumentOptions,
  scenarios: lawScenarios.map((s) => ({
    id: s.id,
    rowId: s.rowId,
    klass: s.klass,
    vehicle: s.vehicle,
    decisiveSourceId: s.decisiveSourceId,
    correctAction: s.correctAction,
    fullCredit: s.fullCredit,
  })),
  assistant: {
    name: 'Matter Assistant',
    subtitle: 'Corporate practice knowledge · no access to the bundle',
    notice:
      'can analyse any document you choose to share. It does not have access to the rest of the matter bundle unless you share it.',
    emptyHint:
      'Use it as much or as little as you want. There is no penalty and no bonus. Share documents from the Source documents tab.',
    chips: [
      'Summarise the key terms of the SPA',
      'Check these documents against the deal instructions',
      'Help me review the completion checklist',
    ],
    placeholder: 'Ask a question, or paste text from a document. Shift + Enter for a new line.',
    shareable: true,
  },
  drawer: {
    whatLabel: 'What requires attention?',
    sourcesLabel: 'Which source establishes it?',
    actions: [
      {
        id: 'query-associate',
        label: 'Query supervising associate',
        hint: 'What you would ask, and why',
      },
      { id: 'raise-other-side', label: 'Raise with other side', hint: 'What you would ask, and why' },
      { id: 'note', label: 'Note only', hint: 'Record the observation, take no action' },
    ],
    entryActions: [],
    escalateActions: ['query-associate', 'raise-other-side'],
    escalateToLabel: 'Raise with',
    amountLabel: 'Amount (GBP)',
    signOffLegend: 'What would you tell the supervising associate about this item?',
    signOffs: [
      { id: 'yes', label: 'Ready for completion' },
      { id: 'escalate', label: 'Ready subject to flagged items' },
      { id: 'no', label: 'Material issues remain' },
    ],
    flagLabel: 'Flag',
    correctLabel: 'Query',
  },
  reflection: {
    heading: 'Reflection',
    intro: 'All four answers are required. Write as much or as little as you need.',
    questions: [
      { key: 'q1', label: 'What did you check against the source documents?' },
      { key: 'q2', label: 'What did you accept from the AI or the bundle without checking, and why?' },
      {
        key: 'q3',
        label: 'Was there anything you initially thought was an issue but later resolved?',
      },
      { key: 'q4', label: 'What would you verify next if you had another 30 minutes?' },
    ],
    confidenceLegend: 'Your recommendation to the supervising associate',
    confidence: [
      { id: 'yes', label: 'Ready for completion' },
      { id: 'with-flags', label: 'Ready subject to the issues I have flagged' },
      { id: 'no', label: 'Material issues remain before completion' },
    ],
    submitLabel: 'Submit review',
    entriesNote: 'Entries stay editable from the bundle review tab until you submit.',
    incompleteNote: 'Answer all four questions and choose a recommendation to enable submission.',
  },
  reply: generateLegalReply,
  evaluate: evaluateLawScenarios,
}

export const instruments: Record<InstrumentId, InstrumentConfig> = {
  'FIN-001': FIN,
  'LAW-001': LAW,
}

export const DEFAULT_INSTRUMENT: InstrumentId = 'FIN-001'

export function getInstrument(id: InstrumentId | null | undefined): InstrumentConfig {
  return instruments[id ?? DEFAULT_INSTRUMENT] ?? FIN
}

export { PACKAGE_ID }
